import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';
import ViewTitle from '../../common/view-title';
import DownloadExcel from '../../common/download-excel';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        marginBottom: 20
    }
}));

const Reports = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [ticketExcelData, setTicketExcelData] = useState([]);
    const [carrierExcelData, setCarrierExcelData] = useState([]);
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        lands: [],
        locations: [],
        filter: {
            code: '',
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            supplier: { id: 0, name: 'All suppliers' },
            land: { id: 0, name: 'All lands' },
            location: { id: 0, name: 'All locations' },
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Reports';

        makeRequest();
    }, []);

    useEffect(() => {
        setTicketExcelData([]);
        setCarrierExcelData([]);
    }, [state.filter]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/reports/indexTickets', state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                suppliers: response?.suppliers ?? [],
                lands: response?.lands ?? [],
                locations: response?.locations ?? []
            }));
        } else {
            console.log('[Reports] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => {
        setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));
    }

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const searchTicketData = async () => {
        showLoader(true);
        const response = await get('/reports/searchTickets', state.filter);
        if (response.status === 200) {
            setTicketExcelData(response?.data ?? []);

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[Reports] searchTicketData error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const searchCarrierData = async () => {
        showLoader(true);
        const response = await get('/reports/searchCarrier', state.filter);
        if (response.status === 200) {
            setCarrierExcelData(response?.data ?? []);

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[Reports] searchCarrierData error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const resetTicketData = () => setTicketExcelData([]);
    const resetCarrierData = () => setCarrierExcelData([]);

    const ticketExcelColumns = [
        { label: 'Master ticket', value: 'master.code' },
        { label: 'Payment method', value: 'master.payment' },
        { label: 'Purchase order', value: 'master.purchase_order' },
        { label: 'MT matching', value: 'match' },
        { label: 'Ticket No.', value: 'code' },
        { label: 'Date', value: 'date' },
        { label: 'Process start date', value: 'process_start_date' },
        { label: 'Status', value: 'status' },
        { label: 'Supplier code', value: 'supplier.code' },
        { label: 'Supplier name', value: 'supplier.name' },
        { label: 'Land code', value: 'land.code' },
        { label: 'Land name', value: 'land.name' },
        { label: 'Land owner', value: 'land.owner.name' },
        { label: 'Sede code (origin)', value: 'sede_origin.code' },
        { label: 'Sede name (origin)', value: 'sede_origin.name' },
        { label: 'Location code', value: 'location.code' },
        { label: 'Location name', value: 'location.name' },
        { label: 'Item name', value: 'detail.item.name' },
        { label: 'Product code', value: 'detail.product_type_ref.product.code' },
        { label: 'Product name', value: 'detail.product_type_ref.product.name' },
        { label: 'Product type name', value: 'detail.product_type_ref.product.product_type.name' },
        { label: 'Product divisibility type', value: 'detail.product_type_ref.type.name' },
        { label: 'Product divisibility qty', value: 'detail.qty_product' },
        { label: 'Price', value: 'detail.final_price' },
        { label: 'Total qty', value: 'detail.total_qty' },
        { label: 'Subtotal', value: 'detail.subtotal' },
        { label: 'Admin fee', value: 'detail.admin_fee' },
        { label: 'Stumpage', value: 'detail.stumpage' },
        { label: 'Extras', value: 'detail.extras' },
        { label: 'Notes', value: 'detail.note' },
        { label: 'Check code', value: 'detail.check.code' },
        { label: 'Invoice number', value: 'detail.check.invoice_number' },
        { label: 'Check status', value: 'detail.check.status' },
        { label: 'Sede code (destination)', value: 'sede.code' },
        { label: 'Sede name (destination)', value: 'sede.name' }
    ]

    const carrierExcelColumns = [
        { label: 'Container code', value: 'carrier.container' },
        { label: 'Purchase order', value: 'purchase_order' },
        { label: 'Master code', value: 'code' },
        { label: 'Matching MT', value: 'match' },
        { label: 'Breakdown code', value: 'tickets.code' },
        { label: 'Date', value: 'date' },
        { label: 'Process start date', value: 'tickets.process_start_date' },
        { label: 'Status', value: 'tickets.status' },
        { label: 'Supplier code', value: 'supplier.code' },
        { label: 'Supplier name', value: 'supplier.name' },
        { label: 'Land code', value: 'land.code' },
        { label: 'Land name', value: 'land.name' },
        { label: 'Land owner', value: 'land.owner.name' },
        { label: 'Sede code', value: 'sede.code' },
        { label: 'Sede name', value: 'sede.name' },
        { label: 'Location code', value: 'location.code' },
        { label: 'Location name', value: 'location.name' },
        { label: 'Item name', value: 'tickets.detail.item.name' },
        { label: 'Item action', value: 'tickets.detail.item.action' },
        { label: 'Product code', value: 'tickets.detail.product_type_ref.product.code' },
        { label: 'Product name', value: 'tickets.detail.product_type_ref.product.name' },
        { label: 'Product type name', value: 'tickets.detail.product_type_ref.product.product_type.name' },
        { label: 'Product divisibility type', value: 'tickets.detail.product_type_ref.type.name' },
        { label: 'Product divisibility qty', value: 'tickets.detail.qty_product' },
        { label: 'Price', value: 'tickets.detail.final_price' },
        { label: 'Freight', value: 'freight' },
        { label: 'Freight piece', value: 'carrier.freight_per_piece' },
        { label: 'Landed cost', value: 'landed_cost' },
        { label: 'Total qty', value: 'tickets.detail.total_qty' },
        { label: 'Breakdown %', value: 'tickets.carrier_qty_percentage' },
        { label: 'Admin fee', value: 'tickets.detail.admin_fee' },
        { label: 'Stumpage', value: 'tickets.detail.stumpage' },
        { label: 'Extras', value: 'tickets.detail.extras' },
        { label: 'Notes', value: 'tickets.detail.note' },
        { label: 'Carrier name', value: 'carrier.carrier_catalog.name' },
        { label: 'Carrier amount', value: 'carrier.amount' }
    ]

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Reports' />
            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.suppliers}
                            value={state.filter.supplier}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('supplier', value)}
                            renderInput={params => <TextField {...params} placeholder='Supplier' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Land'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.lands}
                            value={state.filter.land}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('land', value)}
                            renderInput={params => <TextField {...params} placeholder='Land' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.locations}
                            value={state.filter.location}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('location', value)}
                            renderInput={params => <TextField {...params} placeholder='Location' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                <DownloadExcel
                    buttonsNames={['Search (Tickets)', 'Download excel (Tickets)']}
                    fileName='ticket-report'
                    sheetName='Tickets'
                    data={ticketExcelData}
                    columns={ticketExcelColumns}
                    onClick={searchTicketData}
                    onReset={resetTicketData}
                />
            </div>

            <div className={classes.buttonsContainer}>
                <DownloadExcel
                    buttonsNames={['Search (Carriers)', 'Download excel (Carriers)']}
                    fileName='ticket-carrier-report'
                    sheetName='Carriers'
                    data={carrierExcelData}
                    columns={carrierExcelColumns}
                    onClick={searchCarrierData}
                    onReset={resetCarrierData}
                />
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
                minDate={dayjs('2022-01-01').toDate()}
            />
        </div>
    );
}

export default Reports;